import { useCallback, useContext, useEffect, useRef, useState } from "react";
import "../../styles/layouts/searchbar.scss";
import { SpaceModel } from "../../interfaces/Space";
import { OrientationContext } from '../../contexts/OrientationContext';

const symbolsReplaceRegex = /[\.\,\!\@\#\$\%\^\&\*\|\?\!\-\_]/g
const diacriticRegex = /\p{Diacritic}/gu;
const addressRemove = /\b(de|da|das|do|dos|avenida|rua|travessa|beco|arruamento|lugar)\b/gi;


type Props = {
    spaceList: SpaceModel[]
    pickedSpace: SpaceModel | undefined
    isPicked: boolean
    mapClicked: number
    routeMode: boolean,
    isSpacePicked: boolean,
    setPickedSpace: (space: SpaceModel) => void
    actionStatus: boolean,
    onClickAction: (force: boolean | undefined) => void
}

type SearchCache = {
    id?: string,
    originalName: string,
    name: string,
    addressSearch: string,
    agentsSearch: string,
    normalized: string[]
}

export default function SearchBar({
    spaceList,
    pickedSpace,
    setPickedSpace,
    actionStatus,
    onClickAction,
    isSpacePicked,
    routeMode,
    mapClicked,
}: Props) {

    const isLandscapeMode = useContext(OrientationContext);

    const searchValue = useRef("");
    const [isSearching, setIsSearching] = useState(false);
    const [searchOptions, setSearchOptions] = useState([]);
    const [searchOptionsObjs, setSearchOptionsObjs] = useState<SearchCache[]>([]);
    const [pickedFeature, setPickedFeature] = useState<any>(undefined);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [hasSpaces, setHasSpaces] = useState(false);
    const searchInput = useRef<any>(null);
    const mayListenToKeyDown = useRef(true);



    const closeDropDown = useCallback(() => {
        setDropdownOpen(false);
        searchValue.current = "";
        setIsSearching(false);
    }, []);

    const cancelSearch = useCallback(() => {
        closeDropDown();
        setIsSearching(false);
    }, [closeDropDown]);


    const [searchList, setSearchList] = useState<SearchCache[]>([]);
    const cacheSearchList = useCallback(async (spaces: SpaceModel[]) => {

        const res: SearchCache[] = spaces.map((spc) => {
            const upperCase = spc.properties.name.toUpperCase();
            const normalized = upperCase.normalize("NFD").replaceAll(diacriticRegex, "");
            const normalizedNoSymb = normalized.replaceAll(symbolsReplaceRegex, "");
            const normalizedSymbSpace = normalized.replaceAll(symbolsReplaceRegex, " ").replaceAll(" ", "");
            //address related search
            let addressSearch = spc.mainAddress ? spc.mainAddress?.partOne : '';
            addressSearch = addressSearch.toUpperCase().normalize("NFD").replaceAll(diacriticRegex, "").split(" ").filter((v) => !v.match(addressRemove)).join(" ");

            //agent related search
            let agentsSearch = '';
            if (spc.agentRoles && spc.agentRoles.length > 0) {
                agentsSearch = spc.agentRoles.reduce((acc, curr) => {
                    return `${acc} ${curr.agents.join(' ')}`;
                }, '')
            } else if (spc.properties.agents) {
                agentsSearch = spc.properties.agents.join(' ');
            }

            if (spc.pastAgents) {
                agentsSearch = `${agentsSearch} ${spc.pastAgents.join(' ')}`
            }

            agentsSearch = agentsSearch.toUpperCase().normalize("NFD").replaceAll(diacriticRegex, "")

            const normalizedObj = {
                id: spc.id,
                originalName: spc.properties.name,
                name: upperCase,
                addressSearch,
                agentsSearch,
                normalized: [normalized, normalizedNoSymb, normalizedSymbSpace],
            }

            return normalizedObj;
        }, [])

        setSearchList(res);
    }, []);

    const onSearchOptionClick = useCallback((e: any) => {
        const id = e.target.id;
        const picked: SpaceModel = spaceList.find((spc) => {
            return spc.id === id;
        })!;

        setPickedFeature(picked);
        closeDropDown();

        setPickedSpace(picked);
        if (routeMode) {
            setPickedFeature(undefined);
        }
    }, [closeDropDown, routeMode, setPickedSpace, spaceList])

    const searchFeatures = useCallback(
        (value?: any) => {
            const so: any = [];
            const soObj: SearchCache[] = [];
            // const names = [];
            searchList.forEach((feat: SearchCache) => {
                if (
                    !value ||
                    feat.name.indexOf(value.toUpperCase().trim()) !== -1 ||
                    feat.normalized.find((norm: string) => norm.indexOf(value.toUpperCase().trim()) !== -1) ||
                    feat.agentsSearch.indexOf(value.toUpperCase()) !== -1 ||
                    value.toUpperCase().trim().split(" ").find((v: string) => !v.match(addressRemove) && feat.addressSearch.indexOf(v) !== -1)
                ) {
                    soObj.push(feat);
                    so.push(
                        <div onClick={onSearchOptionClick} key={feat.id} id={feat.id}>
                            {feat.originalName}
                        </div>
                    );
                }
            });

            setSearchOptionsObjs(soObj);
            return so;
        },
        [searchList, onSearchOptionClick]
    );

    const openDropDown = useCallback(() => {
        setDropdownOpen(true);
        setSearchOptions(searchFeatures());
        setIsSearching(true);

        if (searchInput.current) {
            searchInput.current.focus();
        }
    }, [searchFeatures]);

    const onSearch = useCallback((value: any) => {
        setSearchOptions(
            !value || value === "" ? searchFeatures() : searchFeatures(value)
        );
        setIsSearching(true);
    }, [searchFeatures])

    const onSearchChange = useCallback((e: any) => {
        searchValue.current = e.target.value;
        onSearch(e.target.value);
    }, [onSearch]);



    useEffect(() => {
        const container = document.getElementsByClassName('searchBarContainer')[0];
        window.addEventListener("click", function (event: any) {
            if (container !== event.target && !container.contains(event.target)) {
                // console.log('clicking outside the div');
            }
        });
    }, [])

    useEffect(() => {
        setPickedFeature(undefined);
        setHasSpaces(spaceList && spaceList.length > 0);
        setSearchOptions([]);
        setDropdownOpen(false);
        setIsSearching(false);
        cacheSearchList(spaceList);
    }, [cacheSearchList, spaceList]);

    useEffect(() => {
        if (pickedFeature) {
            // setSearchValue(pickedFeature.properties.name);
            setDropdownOpen(false);
            setSearchOptions([]);
            setIsSearching(false);
        }
    }, [pickedFeature]);

    const keyListener = useCallback(
        (e: any) => {
            if (e.key === "Escape") {
                if (!e.repeat && mayListenToKeyDown.current) {
                    mayListenToKeyDown.current = false;
                    cancelSearch();

                    //hack to prevent multiple cancel calls
                    setTimeout(() => {
                        mayListenToKeyDown.current = true;
                    }, 50);
                }

                return;
            }

            if (!actionStatus && e.key === "Enter" && searchOptions && searchOptions.length > 0) {
                onSearchOptionClick({ target: { id: searchOptionsObjs[0].id } })
            }

            if (!actionStatus &&
                ((e.keyCode >= 48 && e.keyCode <= 57) ||
                    (e.keyCode >= 65 && e.keyCode <= 90) ||
                    (e.keyCode >= 97 && e.keyCode <= 122))
            ) {
                if (!isSearching) {
                    openDropDown();
                    setIsSearching(true);
                }

                if (searchInput.current) {
                    searchInput.current.focus();
                }
            }
        },
        [actionStatus, cancelSearch, isSearching, onSearchOptionClick, openDropDown, searchOptions, searchOptionsObjs]
    );

    useEffect(() => {
        window.addEventListener("keyup", keyListener);

        return () => {
            window.removeEventListener("keyup", keyListener);
        };
    }, [keyListener]);

    useEffect(() => {
        setPickedFeature(pickedSpace);
        setIsSearching(false);

        if (!pickedSpace) {
            closeDropDown();
        }
    }, [closeDropDown, pickedSpace]);

    useEffect(() => {
        searchValue.current = "";
        setIsSearching(false);
        closeDropDown();
    }, [closeDropDown, isSpacePicked, mapClicked]);

    useEffect(() => {
        if (searchInput.current) {
            searchInput.current.focus();
        }
    }, [isSearching]);

    useEffect(() => {
        closeDropDown();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeMode]);

    return (
        <div className={`searchBarContainer ${!dropdownOpen ? `closed` : ``}`}>
            <div
                className="searchBar"
                onClick={() => {
                    if (!hasSpaces) return;
                    onClickAction(false);
                    //!dropdownOpen && openDropDown();
                    if (!actionStatus && !dropdownOpen) {
                        openDropDown();
                    }
                }}
            >
                <div className="searchBarInner">
                    {pickedFeature && !isSearching ? (
                        <div className="searchBarContent">
                            <div className="searchBarText">
                                {pickedFeature.properties.name}
                            </div>
                        </div>
                    ) : (
                        !isSearching && (
                            <div
                                className={`${routeMode ? `addPlaceholder` : `searchPlaceholder`
                                    }`}
                            ></div>
                        )
                    )}
                </div>

                {isSearching && (
                    <div className="searchInputMasterContainer">
                        <div className="searchInputContainer">
                            <input
                                placeholder="Espaços, lugares, pessoas..."
                                className="searchInput"
                                ref={searchInput}
                                value={searchValue.current}
                                onChange={onSearchChange}
                            />
                            <div onClick={cancelSearch} className="searchCancelIcon"></div>
                        </div>
                    </div>
                )}

                {dropdownOpen && <div className="searchOptions">{searchOptions}</div>}
            </div>
            {!routeMode && !isLandscapeMode && pickedSpace && !isSearching &&
                <div className="webContentButtons">
                    {(
                        <div
                            onClick={() => onClickAction(undefined)}
                            className={`${actionStatus ? `arrow-up` : `arrow-down`}`}
                        ></div>
                    )}
                </div>}
        </div>
    );
}
